import i18n from '../i18n';
import type { Arches, CaseItem, Package, WorkType } from '../api/types';

export type WorkKind = 'aligner' | 'arch' | 'teeth';

export interface WorkTypeDef {
  kind: WorkKind;
  /** Значения материалов — данные заказа, уходят в API как есть. */
  materials?: string[];
  shade?: boolean;
}

export const WORK_TYPES: Record<WorkType, WorkTypeDef> = {
  aligners: { kind: 'aligner' },
  retainer: { kind: 'arch', materials: ['Термоформованный PETG', 'Несъёмный, титановая проволока'] },
  nightguard: { kind: 'arch', materials: ['Жёсткая акриловая', 'Двухслойная мягкая/жёсткая'] },
  crown: {
    kind: 'teeth',
    materials: ['Цирконий', 'E.max (дисиликат лития)', 'Металлокерамика', 'PMMA временная'],
    shade: true,
  },
  bridge: { kind: 'teeth', materials: ['Цирконий', 'Металлокерамика', 'PMMA временная'], shade: true },
  veneer: { kind: 'teeth', materials: ['E.max (дисиликат лития)', 'Полевошпатная керамика'], shade: true },
  inlay: { kind: 'teeth', materials: ['E.max (дисиликат лития)', 'Цирконий', 'Композит'], shade: true },
  abutment: { kind: 'teeth', materials: ['Титан', 'Цирконий на Ti-base'] },
  guide: { kind: 'teeth', materials: ['Фотополимер, биосовместимый'] },
};

export const WORK_TYPE_KEYS = Object.keys(WORK_TYPES) as WorkType[];
export const PACKAGE_KEYS: Package[] = ['lite', 'moderate', 'full'];
export const ARCH_KEYS: Arches[] = ['both', 'upper', 'lower'];
export const TRAY_TYPES: WorkType[] = ['aligners', 'retainer', 'nightguard'];

/** Цвета VITA classical: код и цвет кнопки. */
export const SHADES: [string, string][] = [
  ['A1', '#F1E6CF'],
  ['A2', '#EBDDBF'],
  ['A3', '#E4D1AE'],
  ['A3.5', '#DCC59F'],
  ['A4', '#D0B78D'],
  ['B1', '#F2EAD6'],
  ['B2', '#EADFC3'],
  ['B3', '#E0D0A8'],
  ['C1', '#E6DFCC'],
  ['C2', '#D9CFB5'],
  ['D2', '#E1D7C0'],
  ['BL2', '#F7F3EA'],
];

/** Верхняя челюсть справа налево пациента, затем нижняя. */
export const FDI_UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
export const FDI_LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
export const FDI_ORDER = [...FDI_UPPER, ...FDI_LOWER];

export const fdiSort = (a: number, b: number): number => FDI_ORDER.indexOf(a) - FDI_ORDER.indexOf(b);
export const isValidFdi = (n: number): boolean => FDI_ORDER.includes(n);

export const workLabel = (type: WorkType): string => i18n.t(`works.types.${type}`);
export const archLabel = (a: Arches): string => i18n.t(`works.arches.${a}`);
export const packageLabel = (p: Package): string => i18n.t(`works.packages.${p}`);

/** Краткое описание работы, как describeWork в прототипе. */
export function describeWork(w: CaseItem): string {
  const t = WORK_TYPES[w.type];
  if (!t) return '';
  if (t.kind === 'aligner') {
    return i18n.t('works.describe.aligner', {
      arches: w.arches ? archLabel(w.arches) : '—',
      pkg: w.package ? packageLabel(w.package) : '—',
      attach: i18n.t(w.attachments ? 'works.describe.attachYes' : 'works.describe.no'),
      ipr: i18n.t(w.ipr ? 'works.describe.iprYes' : 'works.describe.no'),
    });
  }
  if (t.kind === 'arch') {
    return [w.arches ? archLabel(w.arches) : null, w.material].filter(Boolean).join('; ');
  }
  const parts: string[] = [];
  if (w.teeth?.length) parts.push(i18n.t('works.describe.teeth', { teeth: w.teeth.join(', ') }));
  if (w.material) parts.push(w.material);
  if (t.shade && w.shade) parts.push(i18n.t('works.describe.shade', { shade: w.shade }));
  return parts.join('; ');
}

/** «Коронка 36, 37» — короткая строка для таблиц и карточек. */
export const shortWork = (w: CaseItem): string =>
  workLabel(w.type) + (w.teeth?.length ? ' ' + w.teeth.join(', ') : '');

/** Чек-лист производства в прототипе зависит от того, только ли это каппы. Здесь — для шаблона плана. */
export const onlyTrays = (items: CaseItem[]): boolean => items.every((w) => TRAY_TYPES.includes(w.type));

/** Шаблон текста плана для техника (planTemplate прототипа). */
export function planTemplate(items: CaseItem[]): string {
  const al = items.find((w) => w.type === 'aligners');
  if (al) return i18n.t(al.ipr ? 'lab.plan.templateAlignersIpr' : 'lab.plan.templateAligners');
  return i18n.t('lab.plan.templateTeeth', {
    works: items
      .map((w) => workLabel(w.type).toLowerCase() + (w.teeth?.length ? ' ' + w.teeth.join(', ') : ''))
      .join('; '),
  });
}
